/* The willow.tech logo lockup, copied from willow-website src/components/Logo.tsx. */
import type { CSSProperties } from 'react';

/* Geometry from the master lockup: the mark is a 100x100 square, and these
   ratios place the wordmark against it exactly as the brand files do. */
const WORDMARK_HEIGHT = 0.5;   // x mark size
const WORDMARK_ASPECT = 3.638960;  // wordmark width / wordmark height
const WORDMARK_GAP = 0.246995;     // x mark size

type Props = {
  /** Height of the icon (and approx wordmark height). */
  size?: number;
  /** Render the wordmark "willow" next to the icon. */
  withWordmark?: boolean;
  /** Color of the icon (defaults to currentColor). */
  iconColor?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

export function Logo({
  size = 32,
  withWordmark = true,
  iconColor,
  className,
  style,
  ariaLabel = 'Willow',
}: Props) {
  return (
    <span
      className={`brand-logo${className ? ' ' + className : ''}`}
      style={{ gap: size * WORDMARK_GAP, ...style }}
      role="img"
      aria-label={ariaLabel}
    >
      <WillowMark size={size} color={iconColor} />
      {withWordmark && (
        <WillowWordmark height={size * WORDMARK_HEIGHT} />
      )}
    </span>
  );
}

function WillowMark({ size, color }: { size: number; color?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      fill={color ?? 'currentColor'}
      fillRule="evenodd"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M 0 38.27 C 0 28.12 4.032 18.386 11.209 11.209 C 18.386 4.032 28.12 0 38.27 0 L 100 0 L 100 61.73 C 100 71.88 95.968 81.614 88.791 88.791 C 81.614 95.968 71.88 100 61.73 100 L 0 100 Z M 32.75 32.75 L 67.25 32.75 L 67.25 67.25 L 32.75 67.25 Z" />
    </svg>
  );
}

// Wordmark fill comes from .brand-wordmark, so it follows the theme.
function WillowWordmark({ height }: { height: number }) {
  return (
    <svg
      width={height * WORDMARK_ASPECT}
      height={height}
      viewBox="0 0 181.948 50"
      className="brand-wordmark"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M 11.361 49.093 L 0 16.458 L 5.616 16.458 L 13.715 41.382 L 13.844 41.382 L 21.944 16.458 L 26.76 16.458 L 35.054 41.382 L 35.183 41.382 L 43.283 16.458 L 48.704 16.458 L 37.473 49.093 L 32.441 49.093 L 24.428 24.168 L 24.298 24.168 L 16.328 49.093 Z M 54.457 49.093 L 54.457 16.458 L 59.533 16.458 L 59.533 49.093 Z M 53.14 3.866 C 53.14 2.775 53.507 1.857 54.241 1.123 C 54.987 0.378 55.905 0 56.984 0 C 58.075 0 58.993 0.378 59.727 1.123 C 60.473 1.857 60.851 2.775 60.851 3.866 C 60.851 4.946 60.473 5.864 59.727 6.609 C 58.993 7.343 58.075 7.711 56.984 7.711 C 55.905 7.711 54.987 7.343 54.241 6.609 C 53.507 5.864 53.14 4.946 53.14 3.866 Z M 68.949 49.093 L 68.949 0 L 74.025 0 L 74.025 49.093 Z M 83.441 49.093 L 83.441 0 L 88.516 0 L 88.516 49.093 Z M 130.027 32.764 C 130.027 34.957 129.665 37.073 128.947 39.114 C 128.24 41.161 127.165 43.013 125.729 44.665 C 124.287 46.307 122.522 47.613 120.438 48.574 C 118.348 49.519 115.934 50 113.202 50 C 110.232 50 107.684 49.519 105.556 48.574 C 103.424 47.613 101.679 46.307 100.33 44.665 C 98.99 43.013 97.986 41.161 97.327 39.114 C 96.679 37.073 96.356 34.957 96.356 32.764 C 96.356 30.594 96.712 28.483 97.435 26.436 C 98.154 24.395 99.228 22.549 100.654 20.907 C 102.09 19.255 103.855 17.948 105.945 16.998 C 108.029 16.037 110.448 15.551 113.202 15.551 C 116.15 15.551 118.693 16.037 120.826 16.998 C 122.954 17.948 124.698 19.255 126.053 20.907 C 127.403 22.549 128.407 24.395 129.055 26.436 C 129.703 28.483 130.027 30.594 130.027 32.764 Z M 101.841 32.764 C 101.841 35.216 102.295 37.397 103.202 39.309 C 104.12 41.226 105.432 42.732 107.133 43.823 C 108.828 44.903 110.853 45.443 113.202 45.443 C 115.546 45.443 117.57 44.903 119.271 43.823 C 120.967 42.732 122.273 41.226 123.18 39.309 C 124.098 37.397 124.563 35.216 124.563 32.764 C 124.563 30.291 124.098 28.11 123.18 26.22 C 122.273 24.32 120.967 22.829 119.271 21.749 C 117.57 20.659 115.546 20.108 113.202 20.108 C 110.853 20.108 108.828 20.659 107.133 21.749 C 105.432 22.829 104.12 24.32 103.202 26.22 C 102.295 28.11 101.841 30.291 101.841 32.764 Z M 144.604 49.093 L 133.244 16.458 L 138.859 16.458 L 146.959 41.382 L 147.088 41.382 L 155.188 16.458 L 160.004 16.458 L 168.298 41.382 L 168.427 41.382 L 176.527 16.458 L 181.948 16.458 L 170.717 49.093 L 165.684 49.093 L 157.671 24.168 L 157.542 24.168 L 149.572 49.093 Z" />
    </svg>
  );
}
